import { useCallback, useMemo, useState } from 'react';
import { useSnackbar } from 'notistack';
import {
  DOCUMENT_REQUEST_CONTACT_COOLDOWN,
  DOCUMENT_REQUEST_CONTACT_ERROR,
  DOCUMENT_REQUEST_CONTACT_INVALID,
  DOCUMENT_REQUEST_CONTACT_LIMIT,
  DOCUMENT_REQUEST_CONTACT_MAX_COUNT,
  DOCUMENT_REQUEST_CONTACT_SUCCESS,
  DOCUMENT_REQUEST_EXPIRED_ACTION_LABEL,
} from 'common/constants/documents';
import { QUESTIONS_URL_PARAM } from 'common/constants/process-management';
import { Button } from 'components/ui';
import FilesService from 'services/documents';
import TasksService from 'services/tasks';
import { Logger } from 'utilities';

type PortalContactUsButtonProps = {
  requestId: string;
  hmacToken: string;
  processType?: string;
};

type PortalContactErrorBody = {
  error_code?: string;
  contact_count?: number;
  next_contact_available_at?: string;
};

type ApiErrorLike = {
  status?: number;
  response?: {
    status?: number;
    body?: PortalContactErrorBody;
  };
};

type PortalContactSuccessResponse = {
  contact_count?: number;
  next_contact_available_at?: string;
};

const getErrorStatus = (error: unknown): number | undefined => {
  if (!error || typeof error !== 'object') {
    return undefined;
  }
  const apiError = error as ApiErrorLike;
  return apiError.response?.status ?? apiError.status;
};

const getErrorBody = (error: unknown): PortalContactErrorBody | undefined => {
  if (!error || typeof error !== 'object') {
    return undefined;
  }
  return (error as ApiErrorLike).response?.body;
};

const formatNextAvailableDate = (isoDate: string): string => {
  const date = new Date(isoDate);
  if (Number.isNaN(date.getTime())) {
    return isoDate;
  }
  return date.toLocaleDateString(undefined, {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
};

const PortalContactUsButton = ({ requestId, hmacToken, processType }: PortalContactUsButtonProps) => {
  const { enqueueSnackbar } = useSnackbar();
  const filesService = useMemo(() => new FilesService(), []);
  const tasksService = useMemo(() => new TasksService(), []);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLimitReached, setIsLimitReached] = useState(false);
  const [nextContactAvailableAt, setNextContactAvailableAt] = useState<string | null>(null);

  const isCoolingDown = Boolean(
    nextContactAvailableAt && new Date(nextContactAvailableAt).getTime() > Date.now()
  );

  const handleContactUs = useCallback(async () => {
    if (!requestId || !hmacToken || isSubmitting || isCoolingDown || isLimitReached) {
      return;
    }

    setIsSubmitting(true);
    try {
      const response = (
        processType === QUESTIONS_URL_PARAM
          ? await tasksService.contactRequestingUserFromPortal(requestId, hmacToken)
          : await filesService.contactRequestingUserFromPortal(requestId, hmacToken)
      ) as PortalContactSuccessResponse;

      if (response?.next_contact_available_at) {
        setNextContactAvailableAt(response.next_contact_available_at);
      }
      if ((response?.contact_count ?? 0) >= DOCUMENT_REQUEST_CONTACT_MAX_COUNT) {
        setIsLimitReached(true);
      }
      enqueueSnackbar(DOCUMENT_REQUEST_CONTACT_SUCCESS, { variant: 'success' });
    } catch (error) {
      Logger.error('Error contacting requesting user from portal', error);
      const status = getErrorStatus(error);
      const body = getErrorBody(error);
      const cooldownUntil = body?.next_contact_available_at;
      const isLimitError
        = body?.error_code === 'portal_contact_limit'
        || (body?.contact_count ?? 0) >= DOCUMENT_REQUEST_CONTACT_MAX_COUNT;

      if (status === 429 && isLimitError) {
        setIsLimitReached(true);
        enqueueSnackbar(DOCUMENT_REQUEST_CONTACT_LIMIT, { variant: 'info' });
      } else if (status === 429 && cooldownUntil) {
        setNextContactAvailableAt(cooldownUntil);
        enqueueSnackbar(DOCUMENT_REQUEST_CONTACT_COOLDOWN(formatNextAvailableDate(cooldownUntil)), {
          variant: 'info',
        });
      } else if (status === 401) {
        enqueueSnackbar(DOCUMENT_REQUEST_CONTACT_INVALID, { variant: 'error' });
      } else {
        enqueueSnackbar(DOCUMENT_REQUEST_CONTACT_ERROR, { variant: 'error' });
      }
    } finally {
      setIsSubmitting(false);
    }
  }, [
    enqueueSnackbar,
    filesService,
    hmacToken,
    isCoolingDown,
    isLimitReached,
    isSubmitting,
    processType,
    requestId,
    tasksService,
  ]);

  return (
    <Button
      disabled={isSubmitting || isCoolingDown || isLimitReached || !requestId || !hmacToken}
      onClick={handleContactUs}
      variant="outlined"
      visualVariant="primaryOutline">
      {isSubmitting ? 'Sending…' : DOCUMENT_REQUEST_EXPIRED_ACTION_LABEL}
    </Button>
  );
};

export default PortalContactUsButton;
